import { type FunctionComponent, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import {
  Button, Checkbox, Input, Link,
} from '@dr.pogodin/react-utils';

import Throbber from 'components/Throbber';
// import LogoApple from 'assets/images/logo-apple-24px.svg';
// import LogoFacebook from 'assets/images/logo-facebook-24px.svg';
// import LogoGoogle from 'assets/images/logo-google-24px.svg';

import useLogin from 'hooks/useLogin';

import slide1 from 'assets/images/admin/slide-transition-1.png';

import slide2 from 'assets/images/admin/slide-transition-2.png';
import slide3 from 'assets/images/admin/slide-transition-3.png';
import slide4 from 'assets/images/admin/slide-transition-4.png';
import IconEye from 'assets/images/icon-eye.svg';

import Logo from 'assets/images/logo-soul-sanctuary-new-110px.svg';

import inputTheme from 'themes/Input/admin/auth.scss';

import ImageSlider from './ImageSlider';

import eyeIconButtonTheme from './eye-icon-button.scss';

// import socialButtonTheme from './social-button.scss';

import S from './style.scss';

const SLIDE_IMAGES = [slide1, slide2, slide3, slide4];

const CmsLoginPage: FunctionComponent = () => {
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const location = useLocation();

  const {
    busy,
    errorMessage,
    login,
    password,
    passwordOk,
    resetPasswordV2,
    setErrorMessage,
    setPassword,
    setUsername,
    username,
    usernameOk,
  } = useLogin({ target: location.pathname });

  const [remember, setRemember] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const [showUsernameError, setShowUsernameError] = useState(false);
  const [showPasswordError, setShowPasswordError] = useState(false);

  if (usernameOk && showUsernameError) setShowUsernameError(false);
  if (passwordOk && showPasswordError) setShowPasswordError(false);

  const resetPassword = () => {
    void (async () => {
      if (usernameOk) {
        setErrorMessage('If provided email is registered, you\'ll receive password reset email shortly');
        await resetPasswordV2('admin');
      } else setShowUsernameError(true);
    })();
  };

  return (
    <div className={S.container}>
      <div styleName="formColumn">
        <Logo />
        <h1 styleName="title">Login</h1>
        <p styleName="subtitle">Login to access your Soul Sanctuary account</p>
        <form autoComplete="on" styleName="loginForm">
          <div styleName="inputWrapper">
            <Input
              autoComplete="email"
              id="username"
              name="username"
              onBlur={() => {
                if (!usernameOk) setShowUsernameError(true);
              }}
              onChange={(e) => {
                setUsername(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (!passwordRef.current) throw Error('Missing reference');
                  if (usernameOk) passwordRef.current.focus();
                  else setShowUsernameError(true);
                }
              }}
              placeholder="Email Address"
              ref={usernameRef}
              theme={inputTheme}
              type="email"
              value={username}
            />
            {showUsernameError ? (
              <div styleName="error">
                Not a valid email.
              </div>
            ) : null}
          </div>
          <div styleName="inputWrapper">
            <Input
              autoComplete="new-password"
              id="password"
              name="password"
              onBlur={() => {
                if (!passwordOk) setShowPasswordError(true);
              }}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (!usernameRef.current) throw Error('Missing reference');
                  if (!passwordOk) setShowPasswordError(true);
                  else if (usernameOk) void login(remember);
                  else usernameRef.current.focus();
                }
              }}
              placeholder="Password"
              ref={passwordRef}
              theme={inputTheme}
              type={showPassword ? 'text' : 'password'}
              value={password}
            />
            <Button
              onClick={() => {
                setShowPassword(!showPassword);
              }}
              theme={eyeIconButtonTheme}
            >
              <IconEye />
            </Button>
            {showPasswordError ? (
              <div styleName="error">
                Not a valid password.
              </div>
            ) : null}
          </div>
          <div styleName="rememberMeAndForgotPassword">
            <Checkbox
              checked={remember}
              label="Remember me"
              onChange={() => {
                setRemember(!remember);
              }}
            />
            <Link onClick={resetPassword} to="">Forgot Password</Link>
          </div>
          {busy ? <div styleName="throbberContainer"><Throbber /></div> : (
            <Button
              disabled={!usernameOk || !passwordOk}
              onClick={() => {
                void login(remember);
              }}
            >
              Login
            </Button>
          )}
          {errorMessage ? (
            <div styleName="error">
              {errorMessage} &mdash; &zwnj;
              <Link
                onClick={resetPassword}
                onContextMenu={(e) => {
                  e.preventDefault();
                  resetPassword();
                }}
                styleName="link"
                to=""
              >
                Restore password
              </Link>
            </div>
          ) : null}
          {

            /*
              NOTE: The team has opted to not implement social login features at
              the moment:
              https://github.com/moreheaddesign/soul-sanctuary/issues/11#issuecomment-3218310857
          <div styleName="separator">
            <div styleName="separatorText">Or connect using</div>
          </div>
          <div styleName="socialButtons">
            <ThemeProvider themes={{ Button: socialButtonTheme }}>
              <Button disabled><LogoFacebook /></Button>
              <Button disabled><LogoGoogle /></Button>
              <Button disabled><LogoApple /></Button>
            </ThemeProvider>
          </div>
            */
          }
        </form>
      </div>
      <div styleName="slideColumn">
        <ImageSlider images={SLIDE_IMAGES} />
      </div>
    </div>
  );
};

export default CmsLoginPage;
